import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { 
  Container, Row, Col, Form, Button, Card, Nav, 
  Table, Badge, Spinner, Modal, Alert
} from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { FiPlus, FiEdit, FiTrash2, FiMail, FiUsers } from 'react-icons/fi';
import { useAuth } from '../utils/AuthContext';

const PeopleManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingTeamId, setEditingTeamId] = useState(null);
  
  // Check if user can manage (admin or manager)
  // Also check isAdmin flag for backwards compatibility
  const canManage = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.isAdmin;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.isAdmin;
  
  // Debug logging
  console.log('PeopleManagement - currentUser:', currentUser);
  console.log('PeopleManagement - isAdmin:', isAdmin, 'canManage:', canManage);
  
  // Form states
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    role: 'user',
    teamId: '',
    plan: 'free'
  });
  
  const [teamData, setTeamData] = useState({
    name: '',
    description: '',
    managers: []
  });
  
  useEffect(() => {
    fetchUsers();
    fetchTeams();
  }, []);
  
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setIsLoading(false);
    }
  };
  
  const fetchTeams = async () => {
    try {
      const response = await axios.get('/api/teams');
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    }
  };
  
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUserId) {
        await axios.put(`/api/users/${editingUserId}`, userData);
        toast.success('User updated successfully');
      } else {
        await axios.post('/api/users', userData);
        toast.success('User added successfully');
      }
      
      // Reset form and refresh data
      resetUserForm();
      fetchUsers();
      setShowUserModal(false);
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.error || 'Failed to save user');
    }
  };
  
  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTeamId) {
        await axios.put(`/api/teams/${editingTeamId}`, teamData);
        toast.success('Team updated successfully');
      } else {
        await axios.post('/api/teams', teamData);
        toast.success('Team added successfully');
      }
      
      // Reset form and refresh data
      resetTeamForm();
      fetchTeams();
      setShowTeamModal(false);
    } catch (error) {
      console.error('Error saving team:', error);
      toast.error(error.response?.data?.error || 'Failed to save team');
    }
  };
  
  const editUser = (user) => {
    setUserData({
      name: user.name,
      email: user.email,
      role: user.role,
      teamId: user.teamId || '',
      plan: user.plan
    });
    setEditingUserId(user._id);
    setShowUserModal(true);
  };
  
  const editTeam = (team) => {
    setTeamData({
      name: team.name,
      description: team.description,
      managers: team.managers || []
    });
    setEditingTeamId(team._id);
    setShowTeamModal(true);
  };
  
  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`/api/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };
  
  const deleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    
    try {
      await axios.delete(`/api/teams/${teamId}`);
      toast.success('Team deleted successfully');
      fetchTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team');
    }
  };
  
  const resetUserForm = () => {
    setUserData({
      name: '',
      email: '',
      role: 'user',
      teamId: '',
      plan: 'free'
    });
    setEditingUserId(null);
  };
  
  const resetTeamForm = () => {
    setTeamData({
      name: '',
      description: '',
      managers: []
    });
    setEditingTeamId(null);
  };
  
  const inviteUser = async (email) => {
    try {
      await axios.post('/api/users/invite', { email });
      toast.success(`Invitation sent to ${email}`);
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation');
    }
  };
  
  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="page-title">People Management</h1>
          <p className="text-muted">Manage users and teams for your organization</p>
        </Col>
      </Row>
      
      <Nav className="nav-tabs mb-4">
        <Nav.Item>
          <Nav.Link 
            className={activeTab === 'users' ? 'active' : ''} 
            onClick={() => setActiveTab('users')}
          >
            Users
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            className={activeTab === 'teams' ? 'active' : ''} 
            onClick={() => setActiveTab('teams')}
          >
            Teams
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {isLoading && activeTab === 'users' ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="h5 mb-0">
                  {isAdmin ? 'User Directory' : 'Team Members'}
                </h2>
                {canManage && (
                  <Button 
                    variant="primary"
                    onClick={() => {
                      resetUserForm();
                      setShowUserModal(true);
                    }}
                  >
                    <FiPlus className="me-2" /> {isAdmin ? 'Add User' : 'Invite Team Member'}
                  </Button>
                )}
              </div>
              
              {!canManage && (
                <Alert variant="info" className="mb-4">
                  <FiUsers className="me-2" />
                  You can view your team members here. Contact your admin to add new members.
                </Alert>
              )}
              
              {/* Users Table */}
              <Card className="shadow-sm">
                <Table responsive hover className="mb-0 people-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th className="d-none d-md-table-cell">Role</th>
                      <th className="d-none d-lg-table-cell">Team</th>
                      <th className="d-none d-lg-table-cell">Plan</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.map(user => {
                        const userTeam = teams.find(team => team._id === user.teamId);
                        
                        return (
                          <tr key={user._id}>
                            <td>{user.name}</td>
                            <td className="text-truncate" style={{maxWidth: '150px'}}>{user.email}</td>
                            <td className="d-none d-md-table-cell">
                              <Badge bg={
                                user.role === 'admin' ? 'danger' : 
                                user.role === 'manager' ? 'success' : 'primary'
                              }>
                                {user.role}
                              </Badge>
                            </td>
                            <td className="d-none d-lg-table-cell">{userTeam?.name || '-'}</td>
                            <td className="d-none d-lg-table-cell">
                              <Badge bg={
                                user.plan === 'enterprise' ? 'danger' : 
                                user.plan === 'pro' ? 'success' : 'secondary'
                              }>
                                {user.plan}
                              </Badge>
                            </td>
                            <td className="text-end">
                              {canManage && (
                                <>
                                  <Button 
                                    variant="light"
                                    size="sm"
                                    className="me-1"
                                    title="Send Invitation"
                                    onClick={() => inviteUser(user.email)}
                                  >
                                    <FiMail />
                                  </Button>
                                  <Button 
                                    variant="light"
                                    size="sm"
                                    className="me-1"
                                    onClick={() => editUser(user)}
                                  >
                                    <FiEdit />
                                  </Button>
                                </>
                              )}
                              {isAdmin && (
                                <Button 
                                  variant="light"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() => deleteUser(user._id)}
                                >
                                  <FiTrash2 />
                                </Button>
                              )}
                              {!canManage && <span className="text-muted">-</span>}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </Card>
            </div>
          )}
          
          {/* Teams Tab */}
          {activeTab === 'teams' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="h5 mb-0">Team Management</h2>
                {isAdmin && (
                  <Button 
                    variant="primary"
                    onClick={() => {
                      resetTeamForm();
                      setShowTeamModal(true);
                    }}
                  >
                    <FiPlus className="me-2" /> Add Team
                  </Button>
                )}
              </div>
              
              {!isAdmin && (
                <Alert variant="info" className="mb-4">
                  <FiUsers className="me-2" />
                  Only administrators can create and manage teams.
                </Alert>
              )}
              
              {/* Teams Grid */}
              <Row>
                {teams.length === 0 ? (
                  <Col>
                    <div className="text-center p-5 rounded border">
                      <p className="mb-0">No teams found. Create your first team to get started.</p>
                    </div>
                  </Col>
                ) : (
                  teams.map(team => {
                    const teamManagers = users.filter(user => team.managers?.includes(user._id));
                    const teamMembers = users.filter(user => user.teamId === team._id);
                    
                    return (
                      <Col md={6} lg={4} key={team._id} className="mb-4">
                        <Card className="h-100 shadow-sm">
                          <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">{team.name}</h5>
                            {isAdmin && (
                              <div>
                                <Button 
                                  variant="light"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => editTeam(team)}
                                >
                                  <FiEdit />
                                </Button>
                                <Button 
                                  variant="light"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() => deleteTeam(team._id)}
                                >
                                  <FiTrash2 />
                                </Button>
                              </div>
                            )}
                          </Card.Header>
                          <Card.Body>
                            <p className="text-muted">{team.description}</p>
                            
                            <div className="mb-3">
                              <h6 className="mb-2">Managers</h6>
                              {teamManagers.length === 0 ? (
                                <p className="text-muted small">No managers assigned</p>
                              ) : (
                                <div className="d-flex flex-wrap gap-1">
                                  {teamManagers.map(manager => (
                                    <Badge key={manager._id} bg="light" text="dark" className="py-1 px-2">
                                      {manager.name}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <div>
                              <h6 className="mb-2">Members</h6>
                              {teamMembers.length === 0 ? (
                                <p className="text-muted small">No members in this team</p>
                              ) : (
                                <p className="mb-0">{teamMembers.length} members</p>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })
                )}
              </Row>
            </div>
          )}
        </>
      )}
      {/* User Modal */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingUserId ? 'Edit User' : 'Add New User'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUserSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="userName">
              <Form.Label>Name</Form.Label>
              <Form.Control 
                type="text" 
                value={userData.name} 
                onChange={(e) => setUserData({...userData, name: e.target.value})}
                required
                placeholder="Enter full name"
              />
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="userEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control 
                type="email" 
                value={userData.email} 
                onChange={(e) => setUserData({...userData, email: e.target.value})}
                required
                placeholder="Enter email address"
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="userRole">
                  <Form.Label>Role</Form.Label>
                  <Form.Select 
                    value={userData.role} 
                    onChange={(e) => setUserData({...userData, role: e.target.value})}
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="userTeam">
                  <Form.Label>Team</Form.Label>
                  <Form.Select 
                    value={userData.teamId} 
                    onChange={(e) => setUserData({...userData, teamId: e.target.value})}
                  >
                    <option value="">None</option>
                    {teams.map(team => (
                      <option key={team._id} value={team._id}>{team.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="userPlan">
              <Form.Label>Plan</Form.Label>
              <Form.Select 
                value={userData.plan} 
                onChange={(e) => setUserData({...userData, plan: e.target.value})}
              >
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingUserId ? 'Update User' : 'Add User'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Team Modal */}
      <Modal show={showTeamModal} onHide={() => setShowTeamModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingTeamId ? 'Edit Team' : 'Create New Team'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleTeamSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="teamName">
              <Form.Label>Team Name</Form.Label>
              <Form.Control 
                type="text" 
                value={teamData.name} 
                onChange={(e) => setTeamData({...teamData, name: e.target.value})}
                required
                placeholder="e.g. Marketing"
              />
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="teamDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3}
                value={teamData.description} 
                onChange={(e) => setTeamData({...teamData, description: e.target.value})}
                placeholder="Optional description of this team"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTeamModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingTeamId ? 'Update Team' : 'Create Team'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default PeopleManagement;
